import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { CompleteStep } from "../components";
import { useCheckInData, useAnalysisGeneration } from "../hooks";
import { ANALYSIS_GENERATION_DELAY } from "../constants";

export function CheckInCompletePage() {
	const { user } = useAuth();
	const location = useLocation();
	const { reflections, totalCheckInsCount, loading } = useCheckInData(user?.id);
	const hasTriggeredAnalysis = useRef(false);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	const {
		generatingAnalysis,
		analysisStatus,
		analysisMessage,
		generatedAnalysis,
		generateAnalysis,
	} = useAnalysisGeneration(user?.id);

	// Trigger analysis generation if user just submitted (only once per submission)
	useEffect(() => {
		const state = location.state as { justSubmitted?: boolean } | null;

		// Reset when location.state changes (new submission) - do this first
		if (!state?.justSubmitted) {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
			hasTriggeredAnalysis.current = false;
			return;
		}

		// Only trigger if we haven't already triggered for this submission and timer isn't already set
		if (
			state.justSubmitted &&
			!hasTriggeredAnalysis.current &&
			!timerRef.current &&
			!generatingAnalysis &&
			analysisStatus === "idle"
		) {
			hasTriggeredAnalysis.current = true;

			timerRef.current = setTimeout(() => {
				timerRef.current = null;
				generateAnalysis().catch((error) => {
					console.error("Error generating analysis:", error);
				});
			}, ANALYSIS_GENERATION_DELAY);
		}

		// Cleanup function
		return () => {
			// Only clean up if we're actually changing away from justSubmitted state
			if (!state?.justSubmitted && timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [location.state?.justSubmitted, generateAnalysis]); // Only depend on location.state and generateAnalysis

	if (!user || loading) {
		return (
			<div className="container mx-auto px-4 py-8">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
				<CompleteStep
					totalCheckInsCount={totalCheckInsCount || 0}
					reflections={reflections}
					analysisStatus={analysisStatus}
					analysisMessage={analysisMessage}
					generatingAnalysis={generatingAnalysis}
					generatedAnalysis={generatedAnalysis}
				/>
			</div>
		</div>
	);
}
